import * as type from '../../../actions/index.types';

// Member self-service slice, fed by the /api/member/* namespace. Deliberately
// separate from InvoiceReducer / ReceiptReducer: those hold the society-wide
// admin lists, and a member opening My Bills must not overwrite them (nor
// inherit their filters).
const initialState = {
    summaryLoading: false,
    summaryData: null,

    flatsLoading: false,
    flatsData: null,

    invoicesLoading: false,
    invoicesData: null,
    invoicesPagination: null,

    invoiceDetailsLoading: false,
    invoiceDetailsData: null,

    paymentsLoading: false,
    paymentsData: null,
    paymentsPagination: null,

    paymentDetailsLoading: false,
    paymentDetailsData: null,

    ledgerLoading: false,
    ledgerData: null,

    // Resident complaints. Kept apart from ComplaintReducer for the same
    // reason as the finance lists: the staff screens must not inherit a
    // member's own list, and vice versa.
    complaintsLoading: false,
    complaintsData: null,
    complaintsPagination: null,

    complaintDetailsLoading: false,
    complaintDetailsData: null,

    complaintEnumsData: null,

    createComplaintLoading: false,
    complaintAttachmentLoading: false,
    complaintCommentLoading: false,
    complaintRatingLoading: false,

    error: null,
};

export function MemberPortalReducer(state = initialState, action) {
    switch (action.type) {
        case type.MEMBER_GET_SUMMARY:
            return { ...state, summaryLoading: true, error: null };
        case type.MEMBER_GET_SUMMARY_SUCCESS:
            return { ...state, summaryLoading: false, summaryData: action.payload, error: null };
        case type.MEMBER_GET_SUMMARY_FAILURE:
            return { ...state, summaryLoading: false, error: action?.message };

        case type.MEMBER_GET_FLATS:
            return { ...state, flatsLoading: true, error: null };
        case type.MEMBER_GET_FLATS_SUCCESS:
            return { ...state, flatsLoading: false, flatsData: action.payload || [], error: null };
        case type.MEMBER_GET_FLATS_FAILURE:
            return { ...state, flatsLoading: false, error: action?.message };

        case type.MEMBER_GET_INVOICES:
            return { ...state, invoicesLoading: true, error: null };
        case type.MEMBER_GET_INVOICES_SUCCESS:
            return {
                ...state,
                invoicesLoading: false,
                invoicesData: action.payload?.rows || [],
                invoicesPagination: action.payload?.pagination || null,
                error: null,
            };
        case type.MEMBER_GET_INVOICES_FAILURE:
            return { ...state, invoicesLoading: false, error: action?.message };

        case type.MEMBER_GET_INVOICE_DETAILS:
            // Clear the previous bill so the detail screen never flashes the
            // last-opened invoice while the new one is in flight.
            return { ...state, invoiceDetailsLoading: true, invoiceDetailsData: null, error: null };
        case type.MEMBER_GET_INVOICE_DETAILS_SUCCESS:
            return { ...state, invoiceDetailsLoading: false, invoiceDetailsData: action.payload, error: null };
        case type.MEMBER_GET_INVOICE_DETAILS_FAILURE:
            return { ...state, invoiceDetailsLoading: false, error: action?.message };

        case type.MEMBER_GET_PAYMENTS:
            return { ...state, paymentsLoading: true, error: null };
        case type.MEMBER_GET_PAYMENTS_SUCCESS:
            return {
                ...state,
                paymentsLoading: false,
                paymentsData: action.payload?.rows || [],
                paymentsPagination: action.payload?.pagination || null,
                error: null,
            };
        case type.MEMBER_GET_PAYMENTS_FAILURE:
            return { ...state, paymentsLoading: false, error: action?.message };

        case type.MEMBER_GET_PAYMENT_DETAILS:
            return { ...state, paymentDetailsLoading: true, paymentDetailsData: null, error: null };
        case type.MEMBER_GET_PAYMENT_DETAILS_SUCCESS:
            return { ...state, paymentDetailsLoading: false, paymentDetailsData: action.payload, error: null };
        case type.MEMBER_GET_PAYMENT_DETAILS_FAILURE:
            return { ...state, paymentDetailsLoading: false, error: action?.message };

        case type.MEMBER_GET_LEDGER:
            // Same reason as the bill detail: a stale statement under a new
            // date range would show a running balance that never existed.
            return { ...state, ledgerLoading: true, ledgerData: null, error: null };
        case type.MEMBER_GET_LEDGER_SUCCESS:
            return { ...state, ledgerLoading: false, ledgerData: action.payload, error: null };
        case type.MEMBER_GET_LEDGER_FAILURE:
            return { ...state, ledgerLoading: false, error: action?.message };

        case type.MEMBER_GET_COMPLAINTS:
            return { ...state, complaintsLoading: true, error: null };
        case type.MEMBER_GET_COMPLAINTS_SUCCESS:
            return {
                ...state,
                complaintsLoading: false,
                complaintsData: action.payload?.rows || [],
                complaintsPagination: action.payload?.pagination || null,
                error: null,
            };
        case type.MEMBER_GET_COMPLAINTS_FAILURE:
            return { ...state, complaintsLoading: false, error: action?.message };

        case type.MEMBER_GET_COMPLAINT_DETAILS:
            // Clear the previous ticket so the detail screen never flashes the
            // last-opened complaint while the new one is in flight.
            return { ...state, complaintDetailsLoading: true, complaintDetailsData: null, error: null };
        case type.MEMBER_GET_COMPLAINT_DETAILS_SUCCESS:
            return { ...state, complaintDetailsLoading: false, complaintDetailsData: action.payload, error: null };
        case type.MEMBER_GET_COMPLAINT_DETAILS_FAILURE:
            return { ...state, complaintDetailsLoading: false, error: action?.message };

        case type.MEMBER_GET_COMPLAINT_ENUMS_SUCCESS:
            return { ...state, complaintEnumsData: action.payload, error: null };
        case type.MEMBER_GET_COMPLAINT_ENUMS_FAILURE:
            return { ...state, error: action?.message };

        case type.MEMBER_CREATE_COMPLAINT:
            return { ...state, createComplaintLoading: true, error: null };
        case type.MEMBER_CREATE_COMPLAINT_SUCCESS:
            return { ...state, createComplaintLoading: false, error: null };
        case type.MEMBER_CREATE_COMPLAINT_FAILURE:
            return { ...state, createComplaintLoading: false, error: action?.message };

        case type.MEMBER_UPLOAD_COMPLAINT_ATTACHMENTS:
            return { ...state, complaintAttachmentLoading: true, error: null };
        case type.MEMBER_UPLOAD_COMPLAINT_ATTACHMENTS_SUCCESS:
            return { ...state, complaintAttachmentLoading: false, error: null };
        case type.MEMBER_UPLOAD_COMPLAINT_ATTACHMENTS_FAILURE:
            return { ...state, complaintAttachmentLoading: false, error: action?.message };

        case type.MEMBER_ADD_COMPLAINT_COMMENT:
            return { ...state, complaintCommentLoading: true, error: null };
        case type.MEMBER_ADD_COMPLAINT_COMMENT_SUCCESS:
            return { ...state, complaintCommentLoading: false, error: null };
        case type.MEMBER_ADD_COMPLAINT_COMMENT_FAILURE:
            return { ...state, complaintCommentLoading: false, error: action?.message };

        case type.MEMBER_RATE_COMPLAINT:
            return { ...state, complaintRatingLoading: true, error: null };
        case type.MEMBER_RATE_COMPLAINT_SUCCESS:
            return { ...state, complaintRatingLoading: false, error: null };
        case type.MEMBER_RATE_COMPLAINT_FAILURE:
            return { ...state, complaintRatingLoading: false, error: action?.message };

        default:
            return state;
    }
}

export default MemberPortalReducer;
